import { useEffect, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiErrorDetail } from "@/lib/api";
import { Lightning } from "@phosphor-icons/react";

export default function AuthPage({ mode = "login" }) {
  const nav = useNavigate();
  const loc = useLocation();
  const { login, register, user } = useAuth();
  const [email, setEmail] = useState(mode === "login" ? "admin@example.com" : "");
  const [password, setPassword] = useState(mode === "login" ? "admin123" : "");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) nav("/dashboard", { replace: true });
  }, [user, nav]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, name);
      nav(loc.state?.from?.pathname || "/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] text-white grid lg:grid-cols-2">
      {/* Left promo panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 border-r border-zinc-800 grid-bg relative">
        <div>
          <div className="flex items-center gap-3">
            <Lightning size={28} weight="fill" className="text-[#00C805]" />
            <div>
              <div className="heading text-2xl font-black tracking-tighter">STRATOS</div>
              <div className="mono text-[10px] text-zinc-500 mt-0.5">/ AUTOMATED TRADING TERMINAL</div>
            </div>
          </div>
        </div>
        <div className="space-y-6">
          <h1 className="heading text-4xl lg:text-5xl font-black tracking-tighter leading-[0.95]">
            Trade the market.<br />
            <span className="text-[#00C805]">Automate the edge.</span>
          </h1>
          <p className="text-zinc-400 text-sm max-w-md leading-relaxed">
            Paper-trade real markets with $100,000. Deploy MA-crossover and RSI strategies as autonomous bots that execute on your behalf 24/5.
          </p>
          <div className="grid grid-cols-3 gap-3 pt-4">
            <div className="border border-zinc-800 p-4">
              <div className="mono text-2xl font-bold text-[#00C805]">$100K</div>
              <div className="text-xs text-zinc-500 mt-1">Starting capital</div>
            </div>
            <div className="border border-zinc-800 p-4">
              <div className="mono text-2xl font-bold">2</div>
              <div className="text-xs text-zinc-500 mt-1">Bot strategies</div>
            </div>
            <div className="border border-zinc-800 p-4">
              <div className="mono text-2xl font-bold">LIVE</div>
              <div className="text-xs text-zinc-500 mt-1">Market data</div>
            </div>
          </div>
        </div>
        <div className="mono text-[10px] text-zinc-600 uppercase tracking-widest">
          Paper Trading · Simulated Execution · Educational Use
        </div>
      </div>

      {/* Right auth form */}
      <div className="flex items-center justify-center p-6 lg:p-12">
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-6">
          <div>
            <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">
              {mode === "login" ? "Access terminal" : "Open account"}
            </div>
            <h2 className="heading text-3xl font-black tracking-tighter mt-2">
              {mode === "login" ? "Welcome back" : "Create account"}
            </h2>
          </div>

          {mode === "register" && (
            <div className="space-y-2">
              <Label htmlFor="name" className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Full name</Label>
              <Input
                id="name" value={name} onChange={(e) => setName(e.target.value)}
                className="bg-zinc-900 border-zinc-700 rounded-sm h-11"
                data-testid="register-name-input"
                placeholder="Alex Morgan"
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="email" className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Email</Label>
            <Input
              id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="bg-zinc-900 border-zinc-700 rounded-sm h-11"
              data-testid={`${mode}-email-input`}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Password</Label>
            <Input
              id="password" type="password" required minLength={6}
              value={password} onChange={(e) => setPassword(e.target.value)}
              className="bg-zinc-900 border-zinc-700 rounded-sm h-11"
              data-testid={`${mode}-password-input`}
            />
          </div>

          {error && (
            <div className="border border-red-900 bg-red-950/40 text-red-400 text-sm px-3 py-2 rounded-sm" data-testid="auth-error">
              {error}
            </div>
          )}

          <Button
            type="submit" disabled={busy}
            className="w-full h-11 bg-[#00C805] hover:bg-[#00b004] text-black font-semibold rounded-sm disabled:opacity-50"
            data-testid={`${mode}-submit-btn`}
          >
            {busy ? "…" : mode === "login" ? "Sign in" : "Create account"}
          </Button>

          <div className="text-center text-sm text-zinc-500">
            {mode === "login" ? (
              <>New trader?{" "}
                <Link to="/register" className="text-white hover:text-[#00C805] underline" data-testid="link-register">
                  Create an account
                </Link>
              </>
            ) : (
              <>Have an account?{" "}
                <Link to="/login" className="text-white hover:text-[#00C805] underline" data-testid="link-login">
                  Sign in
                </Link>
              </>
            )}
          </div>

          {mode === "login" && (
            <div className="border border-zinc-800 p-3 rounded-sm text-[11px] text-zinc-500 mono">
              DEMO · admin@example.com / admin123
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
